/**
 * GoalsTable — cross-client goals table (Story 18.8, AC #6/#9)
 *
 * §7 table chrome (container rounded-[22px] + inline 0.5px border + bg-muted/50
 * + shadow-xs; header bg-background, text-[13px] font-semibold, 0.5px bottom
 * border) — mirrors ClientTableView.tsx. Each row (in order): goal name,
 * status badge (display status), progress bar colored by that same display
 * status with percentage, client name, platform (logo + display name, em-dash
 * when none), metric (humanized key, em-dash when none), interval badge, and
 * a 3-dot actions menu (manage permission only). Status + Progress sit beside
 * the name so the "how's it doing" pair reads together. Every data column is a
 * sortable header (Goal/Status/Progress/Client/Platform/Metric/Interval) with
 * Phosphor SortAscending/SortDescending indicators on the active column.
 *
 * Clicking a row opens GoalDetailDialog — a centered modal pop-up (§4.1), the
 * app's standard detail surface. (Originally an inline row-expansion per AC#9;
 * moved to a modal to match every other detail/confirm surface in the app.)
 * The row signals this with aria-haspopup="dialog".
 */

import {
  Button,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@provenleap/ui";
import {
  DotsThreeVertical,
  SortAscending,
  SortDescending,
  PencilSimple,
  Pause,
  Play,
  Trash,
} from "@phosphor-icons/react";
import { PICKER_PLATFORMS, logoUrl } from "../../lib/integrations";
import { ClientAvatar } from "../clients/ClientAvatar";
import {
  displayStatus,
  GOAL_STATUS_BADGES,
  INTERVAL_BADGES,
  titleCaseStatus,
  humanizeMetricKey,
  platformDisplayName,
  type GoalListItem,
  type GoalSortKey,
  type SortDirection,
} from "../../lib/goalPageDisplay";
import { GOAL_STATUS_ICONS } from "./goalStatusIcons";
import { formatGoalValue, clampBarFill } from "../../lib/goalFormat";
import { TableHeaderIcon, type TableHeaderIconName } from "../shared/TableHeaderIcon";

interface GoalsTableProps {
  goals: GoalListItem[];
  canManage: boolean;
  /** Open the goal's detail pop-up (GoalDetailDialog) — fired on row click. */
  onView: (goal: GoalListItem) => void;
  sortKey: GoalSortKey;
  sortDir: SortDirection;
  onSort: (key: GoalSortKey) => void;
  onEdit: (goal: GoalListItem) => void;
  onTogglePause: (goal: GoalListItem) => void;
  onDelete: (goal: GoalListItem) => void;
  /** Column ids the user has hidden (from the route, persisted to localStorage). */
  hiddenColumns?: string[];
  /** Retained for call-site compatibility; the Columns toolbar owns visibility. */
  onHideColumn?: (id: string) => void;
}

/**
 * Every data column in render order (Actions is appended separately). Order is
 * triage-first: the priority columns (Goal, Status, Progress, Client) lead
 * left-to-right — this is a cross-client table, so Client is the "whose goal is
 * this" disambiguator, not trailing metadata. The descriptive columns
 * (Platform, Metric, Interval) follow. Every column carries a `key` and renders
 * a sortable th button (Platform/Metric sort by their human label, Interval by
 * an explicit cadence rank: monthly < quarterly < yearly < custom).
 *
 * ⚠️ Do NOT reorder to make the responsive breakpoints climb cleanly. Progress
 * is `md` (it's wide) and DELIBERATELY sits left of the `sm` Client/Platform
 * columns so it stays glued to Status — the progress bar's color is derived from
 * the display status (badge.bar), so Status + Progress are one visual unit.
 * Splitting them to smooth the reflow is the worse trade; the lone cost here is
 * Progress "inserting" between Status and Client at the sm→md step, which is
 * intentional and accepted. Pairing-by-breakpoint still holds (Goal/Status
 * always · Progress+Metric md · Client+Platform sm · Interval lg).
 */
const COLUMNS: {
  key?: GoalSortKey;
  label: string;
  icon: TableHeaderIconName;
  className: string;
  /**
   * The column-show/hide id (matches GOAL_HIDEABLE_COLUMNS + the body <td> gates).
   * Omitted for the pinned Goal (name) column — it can never be hidden.
   */
  hideId?: string;
}[] = [
  { key: "name", label: "Goal", icon: "goal", className: "" },
  { key: "status", label: "Status", icon: "status", className: "whitespace-nowrap", hideId: "status" },
  { key: "progress", label: "Progress", icon: "progress", className: "hidden md:table-cell", hideId: "progress" },
  { key: "client", label: "Client", icon: "client", className: "hidden sm:table-cell whitespace-nowrap", hideId: "client" },
  { key: "platform", label: "Platform", icon: "platform", className: "hidden sm:table-cell whitespace-nowrap", hideId: "platform" },
  { key: "metric", label: "Metric", icon: "metric", className: "hidden md:table-cell whitespace-nowrap", hideId: "metric" },
  { key: "interval", label: "Interval", icon: "interval", className: "hidden lg:table-cell whitespace-nowrap", hideId: "interval" },
];

function platformLogo(platform: string | null) {
  if (!platform) return null;
  const match = PICKER_PLATFORMS.find((p) => p.platform === platform);
  if (!match?.domain) return null;
  return (
    <img
      src={logoUrl(match.domain)}
      alt={match.displayName}
      title={match.displayName}
      className="h-5 w-5 rounded-sm object-contain"
      loading="lazy"
    />
  );
}

export function GoalsTable({
  goals,
  canManage,
  onView,
  sortKey,
  sortDir,
  onSort,
  onEdit,
  onTogglePause,
  onDelete,
  hiddenColumns = [],
}: GoalsTableProps) {
  const isHidden = (id: string) => hiddenColumns.includes(id);
  return (
    <div
      className="overflow-hidden rounded-[22px] bg-muted/50 text-foreground shadow-xs"
      style={{ border: "0.5px solid var(--hairline)" }}
    >
      <table className="app-page-table w-full text-sm" aria-label="Goals list">
        <thead className="relative bg-background">
          <tr>
            {COLUMNS.filter(
              (col) => !col.hideId || !isHidden(col.hideId),
            ).map((col) => {
              const sortButtonOrLabel = col.key ? (
                <button
                  type="button"
                  className="inline-flex items-center gap-1 hover:text-foreground/70 transition-colors cursor-pointer"
                  onClick={() => onSort(col.key!)}
                >
                  <TableHeaderIcon name={col.icon} />
                  {col.label}
                  {sortKey === col.key &&
                    (sortDir === "asc" ? (
                      <SortAscending size={18} />
                    ) : (
                      <SortDescending size={18} />
                    ))}
                </button>
              ) : (
                <span className="inline-flex items-center gap-1.5">
                  <TableHeaderIcon name={col.icon} />
                  {col.label}
                </span>
              );
              return (
                <th
                  key={col.label}
                  scope="col"
                className={`text-left py-2 px-4 text-[13px] font-semibold text-foreground ${col.className}`}
                >
                {sortButtonOrLabel}
                </th>
              );
            })}
            <th scope="col" className="py-2 px-4 w-16">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {goals.map((goal) => {
            const status = displayStatus(goal);
            const badge = GOAL_STATUS_BADGES[status];
            const StatusIcon = GOAL_STATUS_ICONS[status];
            const pct = goal.progress.percentage;
            const fill = clampBarFill(pct);
            const valueLabel = `${formatGoalValue(goal.currentValue, goal.unit, goal.currencyCode)} / ${formatGoalValue(goal.targetValue, goal.unit, goal.currencyCode)}`;
            return (
              <tr
                key={goal.id}
                className="bg-background hover:bg-brand/[0.02] transition-colors cursor-pointer"
                aria-haspopup="dialog"
                onClick={() => onView(goal)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onView(goal);
                  }
                }}
                tabIndex={0}
                data-testid={`goal-row-${goal.id}`}
              >
                {/* 1. Goal — section icon + name (matches "Goal" th, no responsive class) */}
                <td className="py-3 px-4">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="font-medium truncate block" title={goal.name}>
                      {goal.name}
                    </span>
                  </div>
                </td>

                {/* 2. Status — display-status badge (matches "Status" th: whitespace-nowrap) */}
                {!isHidden("status") && (
                  <td className="py-3 px-4 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${badge.badge}`}
                      data-testid={`goal-status-${goal.id}`}
                    >
                      <StatusIcon size={12} weight="bold" />
                      {titleCaseStatus(badge.label)}
                    </span>
                  </td>
                )}

                {/* 3. Progress — bar + percentage + value label (matches "Progress" th: hidden md:table-cell).
                    The bar color (badge.bar) is derived from the same display status as the Status badge. */}
                {!isHidden("progress") && (
                  <td className="py-3 px-4 hidden md:table-cell">
                    <div className="flex items-center gap-2">
                      <div
                        className="h-2 w-32 overflow-hidden rounded-full bg-muted"
                        style={{ border: "0.5px solid var(--hairline)" }}
                        title={valueLabel}
                      >
                        <div
                          className={`h-full rounded-full ${badge.bar}`}
                          style={{ width: `${fill}%` }}
                          data-testid={`goal-bar-${goal.id}`}
                        />
                      </div>
                      <span className="text-[13px] text-foreground tabular-nums">
                        {Math.round(pct)}%
                      </span>
                    </div>
                    <div
                      className="mt-1.5 w-32 text-center text-[12px] text-foreground tabular-nums"
                      data-testid={`goal-value-${goal.id}`}
                    >
                      {valueLabel}
                    </div>
                  </td>
                )}

                {/* 4. Client — avatar (logo) + name (matches "Client" th: hidden sm:table-cell whitespace-nowrap) */}
                {!isHidden("client") && (
                  <td className="py-3 px-4 text-foreground hidden sm:table-cell whitespace-nowrap">
                    <span className="flex items-center gap-2">
                      <ClientAvatar name={goal.clientName} logoUrl={null} size="sm" />
                      <span className="max-w-[140px] truncate" title={goal.clientName}>
                        {goal.clientName}
                      </span>
                    </span>
                  </td>
                )}

                {/* 5. Platform — logo + display name (matches "Platform" th: hidden sm:table-cell whitespace-nowrap) */}
                {!isHidden("platform") && (
                  <td className="py-3 px-4 hidden sm:table-cell whitespace-nowrap" data-testid={`goal-platform-${goal.id}`}>
                    {goal.platform ? (
                      <span className="flex items-center gap-2">
                        {platformLogo(goal.platform)}
                        <span className="text-foreground">{platformDisplayName(goal.platform)}</span>
                      </span>
                    ) : (
                      <span className="text-muted-foreground/60">—</span>
                    )}
                  </td>
                )}

                {/* 6. Metric — humanized metric key (matches "Metric" th: hidden md:table-cell whitespace-nowrap) */}
                {!isHidden("metric") && (
                  <td className="py-3 px-4 hidden md:table-cell whitespace-nowrap" data-testid={`goal-metric-${goal.id}`}>
                    {goal.metricKey ? (
                      <span className="text-foreground">{humanizeMetricKey(goal.metricKey)}</span>
                    ) : (
                      <span className="text-muted-foreground/60">—</span>
                    )}
                  </td>
                )}

                {/* 7. Interval — plain text, normal case, same color/size as the other cells */}
                {!isHidden("interval") && (
                  <td className="py-3 px-4 hidden lg:table-cell whitespace-nowrap">
                    <span className="text-foreground">
                      {INTERVAL_BADGES[goal.periodType]}
                    </span>
                  </td>
                )}

                {/* 8. Actions — 3-dot menu (matches "Actions" th: w-16, right-aligned) */}
                <td
                  className="py-3 px-4 text-right w-16"
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  {canManage && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="min-h-11 min-w-11 cursor-pointer rounded-lg border-[0.5px] border-transparent hover:bg-background hover:border-input transition-all"
                          aria-label={`Actions for ${goal.name}`}
                        >
                          <DotsThreeVertical size={28} weight="bold" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="end"
                        className="rounded-xl p-2 shadow-md"
                        style={{ border: "0.5px solid var(--hairline)" }}
                      >
                        <DropdownMenuItem
                          className="min-h-11 text-[13px] cursor-pointer rounded-lg"
                          onClick={() => onEdit(goal)}
                        >
                          <PencilSimple size={14} className="mr-1" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="min-h-11 text-[13px] cursor-pointer rounded-lg"
                          onClick={() => onTogglePause(goal)}
                        >
                          {goal.status === "paused" ? (
                            <>
                              <Play size={14} className="mr-1" />
                              Resume
                            </>
                          ) : (
                            <>
                              <Pause size={14} className="mr-1" />
                              Pause
                            </>
                          )}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="min-h-11 text-[13px] text-destructive cursor-pointer rounded-lg hover:!bg-destructive/10 focus:!bg-destructive/10 focus:!text-destructive focus:!border-destructive/30"
                          onClick={() => onDelete(goal)}
                        >
                          <Trash size={14} className="mr-1" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
